/**
 * Curated list of ATS providers available via Kombo (kombo.dev — unified
 * HRIS/ATS API). Ordered by approximate market popularity, most popular first.
 *
 * Used by /settings/ats to render an "available integrations" marketplace
 * before any account is connected. `domain` drives the logo lookup chain
 * (Clearbit → DuckDuckGo → Google favicon → colored initials).
 */

export type AtsRegion =
    | 'Global'
    | 'North America'
    | 'Europe'
    | 'United Kingdom'
    | 'Asia-Pacific';

export interface AtsProvider {
    id: string;
    name: string;
    domain: string;
    region: AtsRegion;
    description: string;
    popularRank: number;
}

export const POPULAR_ATS_PROVIDERS: AtsProvider[] = [
    {
        id: 'workday',
        name: 'Workday',
        domain: 'workday.com',
        region: 'Global',
        description: 'Enterprise HCM suite with recruiting and core HR.',
        popularRank: 1,
    },
    {
        id: 'greenhouse',
        name: 'Greenhouse',
        domain: 'greenhouse.io',
        region: 'Global',
        description: 'Structured-hiring ATS popular with mid-market and tech.',
        popularRank: 2,
    },
    {
        id: 'lever',
        name: 'Lever',
        domain: 'lever.co',
        region: 'Global',
        description: 'CRM-style ATS focused on talent relationship management.',
        popularRank: 3,
    },
    {
        id: 'workable',
        name: 'Workable',
        domain: 'workable.com',
        region: 'Global',
        description: 'Full-cycle ATS with sourcing, AI screening, and onboarding.',
        popularRank: 4,
    },
    {
        id: 'icims',
        name: 'iCIMS',
        domain: 'icims.com',
        region: 'Global',
        description: 'Enterprise-grade talent acquisition platform.',
        popularRank: 5,
    },
    {
        id: 'sap-successfactors',
        name: 'SAP SuccessFactors',
        domain: 'sap.com',
        region: 'Global',
        description: 'SAP-native HCM and recruiting suite for large enterprises.',
        popularRank: 6,
    },
    {
        id: 'bullhorn',
        name: 'Bullhorn',
        domain: 'bullhorn.com',
        region: 'Global',
        description: 'CRM + ATS built for staffing and recruitment agencies.',
        popularRank: 7,
    },
    {
        id: 'bamboohr',
        name: 'BambooHR',
        domain: 'bamboohr.com',
        region: 'North America',
        description: 'HRIS with built-in applicant tracking for SMBs.',
        popularRank: 8,
    },
    {
        id: 'smartrecruiters',
        name: 'SmartRecruiters',
        domain: 'smartrecruiters.com',
        region: 'Global',
        description: 'Talent acquisition suite with marketplace integrations.',
        popularRank: 9,
    },
    {
        id: 'ashby',
        name: 'Ashby',
        domain: 'ashbyhq.com',
        region: 'North America',
        description: 'Modern all-in-one ATS, sourcing, and analytics platform.',
        popularRank: 10,
    },
    {
        id: 'recruitee',
        name: 'Recruitee',
        domain: 'recruitee.com',
        region: 'Europe',
        description: 'Collaborative hiring platform popular in EMEA.',
        popularRank: 11,
    },
    {
        id: 'personio',
        name: 'Personio',
        domain: 'personio.com',
        region: 'Europe',
        description: 'European HR and recruiting platform for mid-sized companies.',
        popularRank: 12,
    },
    {
        id: 'jazzhr',
        name: 'JazzHR',
        domain: 'jazzhr.com',
        region: 'North America',
        description: 'Affordable ATS aimed at small businesses.',
        popularRank: 13,
    },
    {
        id: 'teamtailor',
        name: 'Teamtailor',
        domain: 'teamtailor.com',
        region: 'Europe',
        description: 'Employer-branded ATS and career site builder.',
        popularRank: 14,
    },
    {
        id: 'pinpoint',
        name: 'Pinpoint',
        domain: 'pinpointhq.com',
        region: 'United Kingdom',
        description: 'Mid-market ATS with strong analytics and reporting.',
        popularRank: 15,
    },
    {
        id: 'comeet',
        name: 'Comeet',
        domain: 'comeet.com',
        region: 'Global',
        description: 'Collaborative hiring platform with structured workflows.',
        popularRank: 16,
    },
    {
        id: 'recruiterflow',
        name: 'Recruiterflow',
        domain: 'recruiterflow.com',
        region: 'Global',
        description: 'CRM and ATS built for recruiting and staffing agencies.',
        popularRank: 17,
    },
    {
        id: 'manatal',
        name: 'Manatal',
        domain: 'manatal.com',
        region: 'Asia-Pacific',
        description: 'AI-driven recruitment platform popular across APAC.',
        popularRank: 18,
    },
    {
        id: 'oracle-recruiting',
        name: 'Oracle Recruiting Cloud',
        domain: 'oracle.com',
        region: 'Global',
        description: 'Recruiting module of Oracle Fusion HCM (formerly Taleo).',
        popularRank: 19,
    },
    {
        id: 'ukg-pro-recruiting',
        name: 'UKG Pro Recruiting',
        domain: 'ukg.com',
        region: 'North America',
        description: 'Recruiting module within the UKG Pro HCM suite.',
        popularRank: 20,
    },
];

export const ATS_REGIONS: AtsRegion[] = [
    'Global',
    'North America',
    'Europe',
    'United Kingdom',
    'Asia-Pacific',
];
